/**
 * Guards against crashes caused by in-browser machine translation
 * (Google Translate in Chrome, Edge "Translate page", etc.).
 *
 * Those features replace text nodes with <font> wrappers behind React's
 * back. When React later reconciles and calls removeChild/insertBefore
 * with a node the translator already moved, the DOM throws
 * ("Failed to execute 'removeChild' on 'Node'...") and the whole app
 * white-screens. See https://github.com/facebook/react/issues/11538.
 *
 * The patch keeps React's view of the world consistent: if the child has
 * been relocated by the translator, removeChild becomes a no-op and
 * insertBefore falls back to appendChild instead of throwing.
 */
export function installTranslationGuard(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return this.appendChild(newNode) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
